import { AxiosResponse } from 'axios'
import Request from '../config/services/Request'
import {
  CreateBulkQtfProgramParams,
  CreateQtfProgramParams,
  EmployeeAllocationUpdateData,
  UpdateQtfProgramParams,
} from './qtfTypes'

const getAllQtfPrograms = (): Promise<AxiosResponse> => {
  return Request.get(`/hr/tax_benefits/benefits_config/`)
}

const createQtfProgram = async (
  params: CreateQtfProgramParams
): Promise<AxiosResponse[]> => {
  const {
    types,
    commUserIds: _c,
    employerParkingContribution,
    employerTransitContribution,
    employerTransitMaxAllocationLimit,
    employerParkingMaxAllocationLimit,
    ...rest
  } = params

  const allPromises = []

  types.map((type) => {
    allPromises.push(
      Request.post('/hr/tax_benefits/benefits_config/', {
        type,
        employerContribution:
          type === 'COMMUTER_PARKING'
            ? employerParkingContribution
            : employerTransitContribution,
        employerMaxAllocationLimit:
          type === 'COMMUTER_PARKING'
            ? employerParkingMaxAllocationLimit
            : employerTransitMaxAllocationLimit,
        ...rest,
      })
    )
  })

  return await Promise.all(allPromises)
}

const createBulkQtfPrograms = async (
  params: CreateBulkQtfProgramParams
): Promise<AxiosResponse[]> => {
  const {
    types,
    commUserIds: _c,
    employerParkingContribution,
    employerTransitContribution,
    employerTransitMaxAllocationLimit,
    employerParkingMaxAllocationLimit,
    ...rest
  } = params
  const allPromises = []
  types.map((type) => {
    allPromises.push(
      Request.post('/hr/tax_benefits/bulk_benefits_config/', {
        type,
        employerContribution:
          type === 'COMMUTER_PARKING'
            ? employerParkingContribution
            : employerTransitContribution,
        employerMaxAllocationLimit:
          type === 'COMMUTER_PARKING'
            ? employerParkingMaxAllocationLimit
            : employerTransitMaxAllocationLimit,
        ...rest,
      })
    )
  })
  return await Promise.all(allPromises)
}

const updateQtfProgram = async (
  params: UpdateQtfProgramParams
): Promise<AxiosResponse[]> => {
  const {
    typeMap,
    types,
    employerParkingContribution,
    employerTransitContribution,
    employerTransitMaxAllocationLimit,
    employerParkingMaxAllocationLimit,
    ...rest
  } = params
  const allPromises = []
  types.map((type) => {
    const programId = typeMap[type].id
    allPromises.push(
      Request.put(`/hr/tax_benefits/benefits_config/${programId}/`, {
        type,
        employerContribution:
          type == 'COMMUTER_PARKING'
            ? employerParkingContribution
            : employerTransitContribution,
        employerMaxAllocationLimit:
          type == 'COMMUTER_PARKING'
            ? employerParkingMaxAllocationLimit
            : employerTransitMaxAllocationLimit,
        ...rest,
      })
    )
  })
  return await Promise.all(allPromises)
}

const cancelQtfProgram = async (params: string[]): Promise<AxiosResponse[]> => {
  const allPromises = []
  params.map((id) => {
    allPromises.push(Request.del(`/hr/tax_benefits/benefits_config/${id}/`))
  })
  return await Promise.all(allPromises)
}

const getQTFDashboardServices = (params: {
  segmentId: string
}): Promise<AxiosResponse> => {
  const { segmentId } = params
  return Request.get(`/hr/tax_benefits/benefits_config/${segmentId}/services/`)
}

const getQTFDashboardStats = (params: {
  segmentId: string
}): Promise<AxiosResponse> => {
  const { segmentId } = params
  return Request.get(
    `/hr/tax_benefits/benefits_config/${segmentId}/summary_stats/`
  )
}

const getQTFDashboardDeductions = (params: {
  segmentId: string
  format: 'csv' | 'json'
}): Promise<AxiosResponse> => {
  const { segmentId, format } = params
  return Request.get(
    `/hr/tax_benefits/current_deductions/report/?segment=${segmentId}&format=${format}`,
    format === 'csv'
  )
}

const getQTFUpcomingAllocations = (params: {
  segmentId: string
}): Promise<AxiosResponse> => {
  const { segmentId } = params
  return Request.get(
    `/hr/tax_benefits/current_deductions/per_employee/?segment=${segmentId}`
  )
}

const getAllQTFDeductions = (): Promise<AxiosResponse> => {
  return Request.get(`/hr/tax_benefits/current_deductions/report/`)
}

const sendEmployeeReminder = (params: {
  segmentId: string
}): Promise<AxiosResponse> => {
  const { segmentId } = params
  return Request.post(
    `/hr/tax_benefits/benefits_config/${segmentId}/send_reminder/`,
    {}
  )
}

const getProgramCommUsers = (params: {
  segmentId: string
}): Promise<AxiosResponse> => {
  const { segmentId } = params
  return Request.get(
    `/hr/tax_benefits/benefits_config/${segmentId}/user_notifications/`,
    true
  )
}

const updateProgramCommUsers = (params: {
  segmentId: string
  userIds: string[]
}): Promise<AxiosResponse> => {
  const { segmentId, userIds } = params
  return Request.put(
    `/hr/tax_benefits/benefits_config/${segmentId}/user_notifications/`,
    { users: userIds },
    false,
    true
  )
}

const updateEmployeeAllocation = (params: {
  allocationId: string
  updatedAllocation: EmployeeAllocationUpdateData
}): Promise<AxiosResponse> => {
  const { allocationId, updatedAllocation } = params
  return Request.put(
    `/hr/tax_benefits/current_deductions/${allocationId}/`,
    updatedAllocation
  )
}

const createEmployeeAllocation = (
  params: EmployeeAllocationUpdateData
): Promise<AxiosResponse> => {
  const newAllocation = params
  return Request.post('/hr/tax_benefits/current_deductions/', newAllocation)
}

export default {
  getAllQtfPrograms,
  createQtfProgram,
  createBulkQtfPrograms,
  updateQtfProgram,
  cancelQtfProgram,
  getQTFDashboardDeductions,
  getQTFUpcomingAllocations,
  getQTFDashboardServices,
  getQTFDashboardStats,
  getAllQTFDeductions,
  sendEmployeeReminder,
  updateProgramCommUsers,
  getProgramCommUsers,
  updateEmployeeAllocation,
  createEmployeeAllocation,
}
